import * as Yup from "yup";
import moment from "moment";

const today = moment().format("YYYY-MM-DD");
const namePattern = /^[a-zA-Z\u0600-\u06FF\u00C0-\u017F '-]+$/;

export const validationSchema = [
  Yup.object().shape({
    User: Yup.object().shape({
      FirstName: Yup.string()
        .trim()
        .matches(namePattern, "Incorrect format")
        .max(20, "Must be 20 characters or less")
        .required("Required"),
      MiddleName: Yup.string()
        .trim()
        .matches(namePattern, "Incorrect format")
        .max(20, "Must be 20 characters or less")
        .required("Required"),
      FamilyName: Yup.string()
        .trim()
        .matches(namePattern, "Incorrect format")
        .max(20, "Must be 20 characters or less")
        .required("Required"),
      MotherName: Yup.string()
        .trim()
        .matches(namePattern, "Incorrect format")
        .max(20, "Must be 20 characters or less"),
      DateOfBirth: Yup.date()
        .required("Required")
        .test("Is date greater", "DOB cannot be greater than today's date", (value) => {
          if (!value) return true;
          return moment(today).diff(value) > 0;
        })
        .typeError("Invalid Date!"),
      Gender: Yup.string().required("Required"),
      BloodType: Yup.string().required("Required"),
      Identity: Yup.object().shape({
        IdentityID: Yup.string()
          .trim()
          .matches(/^[a-zA-Z0-9]*$/, "Incorrect format")
          .max(20, "Must be 20 characters or less"),
        IdentityImageUri: Yup.string(),
      }),
      MaritalStatus: Yup.string().required("Required"),
    }),
  }),
  Yup.object().shape({
    User: Yup.object().shape({
      Address: Yup.object().shape({
        Street: Yup.string().trim().matches(/^[a-zA-Z0-9.\s]+$/,"Incorrect format").max(200,"Must be 200 characters or less").required("Required"),
        City: Yup.string().trim().matches(/^[a-zA-Z0-9.\s]+$/,"Incorrect format").max(200,"Must be 200 characters or less").required("Required"),
        CountryCode: Yup.string().trim().required("Required"),
        Zip: Yup.string().trim().matches(/^([0-9]{5}(?:-[0-9]{4})?)?$/, "Zip code format is 00000-0000"),
        Additional: Yup.string().trim().matches(/^[a-zA-Z0-9().,\s"-]*$/,"Incorrect format").max(200,"Must be 200 characters or less"),
      }),
    }),
  }),
  Yup.object().shape({
    User: Yup.object().shape({
      Insurance: Yup.object().shape({
        InsuranceNb: Yup.string()
          .trim()
          .matches(/^[a-zA-Z0-9\s]+$/, "Incorrect format")
          .max(20, "Must be 20 characters or less").required("Required"),
        InsuranceCompany: Yup.string()
          .trim()
          .matches(/^[a-zA-Z ']+$/, "Incorrect format").required("Required"),
        InsuranceCountry: Yup.string().trim().required("Required"),
        InsurancePolicy: Yup.string().trim().matches(/^[a-zA-Z0-9\s]+$/, "Incorrect format").max(40, "Must be 40 characters or less").required("Required"),
        IssuedDate: Yup.date().typeError("Invalid format").required("Required"),
        ExpiryDate: Yup.date().typeError("Invalid format").required("Required").min(Yup.ref("IssuedDate"), "Expiry Date should be after the Issued Date"),
        Active: Yup.boolean().required("Required"),
      }),
    }),
  }),
  Yup.object().shape({
    User: Yup.object().shape({      
      Contact: Yup.object().shape({
        PrimaryEmail: Yup.object().shape({
          Email: Yup.string().trim().email("Invalid Email")
        }),
        SecondaryEmail: Yup.object().shape({
          Email: Yup.string().trim().email("Invalid Email"),
        }),
        PrimaryPhone: Yup.object().shape({
          Phone: Yup.string()
            .trim()
            .matches(/^\+(?:[0-9] ?){6,14}[0-9]$/, "Incorrect format")
            .required("Required"),
          CountryCode: Yup.string().trim().required("Required"),
        }),
        SecondaryPhone: Yup.object().shape({
          Phone: Yup.string()
            .trim()
            .matches(/^\+(?:[0-9] ?){6,14}[0-9]$/, {message: "Incorrect format", excludeEmptyString: true}),
          CountryCode: Yup.string().trim(),
        }),
      }),
    }),
  }),
  Yup.object().shape({
    Patient: Yup.object().shape({
      Height: Yup.number()                
        .min(1, "Height can't be 0")
        .max(240, "Height must be 240 or less"),        
      Weight: Yup.number()                
        .min(1, "Weight can't be 0")
        .max(500, "Weight must be 500 or less"),  
      Allergies: Yup.array().of(
        Yup.string()
          .trim()
          .matches(/^[a-zA-Z0-9.\s]+$/, "Invalid")
          .max(100,"Must be 100 characters or less")
          .required("Cannot be empty!")
      ),
      ChronicDiseases: Yup.array().of(
        Yup.string()
          .trim()
          .matches(/^[a-zA-Z0-9.\s]+$/, "Invalid")
          .max(100,"Must be 100 characters or less")
          .required("Required")
      ),
      CriticNotes: Yup.array().of(
        Yup.object().shape({
          DateCreated: Yup.date(),
          DateModified: Yup.date(),
          Note: Yup.string()
            .trim()
            .matches(/^[\s\S]+$/, "Invalid")
            .max(600, "Must be 600 characters or less")
            .required("Cannot be empty!")
        })
      ),
    }),
  }),
];

export const editValidationSchema = [
  Yup.object().shape({
    User: Yup.object().shape({
      FirstName: Yup.string()
        .trim()
        .matches(namePattern, "Incorrect format")
        .max(20, "Must be 20 characters or less")
        .required("Required"),
      MiddleName: Yup.string()
        .trim()
        .matches(namePattern, "Incorrect format")
        .max(20, "Must be 20 characters or less")
        .required("Required"),
      FamilyName: Yup.string()
        .trim()
        .matches(namePattern, "Incorrect format")
        .max(20, "Must be 20 characters or less")
        .required("Required"),
      MotherName: Yup.string()
        .trim()
        .matches(namePattern, "Incorrect format")
        .max(20, "Must be 20 characters or less"),
      DateOfBirth: Yup.date()
        .required("Required")
        .test("Is date greater", "DOB cannot be greater than today's date", (value) => {
          if (!value) return true;
          return moment(today).diff(value) > 0;
        })
        .typeError("Invalid Date!"),
      Gender: Yup.string().required("Required"),
      BloodType: Yup.string().required("Required"),
      Identity: Yup.object().shape({
        IdentityID: Yup.string()
          .trim()
          .matches(/^[a-zA-Z0-9]*$/, "Incorrect format")
          .max(20, "Must be 20 characters or less"),
        IdentityImageUri: Yup.string(),
      }),
      MaritalStatus: Yup.string().required("Required"),
    }),
  }),
  Yup.object().shape({
    User: Yup.object().shape({
      Address: Yup.object().shape({
        Street: Yup.string().trim().matches(/^[a-zA-Z0-9.\s]+$/,"Incorrect format").max(200,"Must be 200 characters or less").required("Required"),
        City: Yup.string().trim().matches(/^[a-zA-Z0-9.\s]+$/,"Incorrect format").max(200,"Must be 200 characters or less").required("Required"),
        CountryCode: Yup.string().trim().required("Required"),
        Zip: Yup.string().trim().matches(/^([0-9]{5}(?:-[0-9]{4})?)?$/, "Zip code format is 00000-0000"),
        Additional: Yup.string().trim().matches(/^[a-zA-Z0-9().,\s"-]*$/,"Incorrect format").max(200,"Must be 200 characters or less"),
      }),
    }),
  }),
  Yup.object().shape({
    User: Yup.object().shape({
      Insurance: Yup.object().shape({
        InsuranceNb: Yup.string()
          .trim()
          .matches(/^[a-zA-Z0-9\s]+$/, "Incorrect format")
          .max(20, "Must be 20 characters or less").required("Required"),
        InsuranceCompany: Yup.string()
          .trim()
          .matches(/^[a-zA-Z ']+$/, "Incorrect format").required("Required"),
        InsuranceCountry: Yup.string().trim().required("Required"),
        InsurancePolicy: Yup.string().trim().matches(/^[a-zA-Z0-9\s]+$/, "Incorrect format").max(40, "Must be 40 characters or less").required("Required"),
        IssuedDate: Yup.date().typeError("Invalid format").required("Required"),
        ExpiryDate: Yup.date().typeError("Invalid format").required("Required").min(Yup.ref("IssuedDate"), "Expiry Date should be after the Issued Date"),
        Active: Yup.boolean().required("Required"),
      }),
    }),
  }),
  Yup.object().shape({
    User: Yup.object().shape({
      Contact: Yup.object().shape({
        PrimaryEmail: Yup.object().shape({
          Email: Yup.string().trim().email("Invalid Email").required("Required"),
        }),
        SecondaryEmail: Yup.object().shape({
          Email: Yup.string().trim().email("Invalid Email"),
        }),
        PrimaryPhone: Yup.object().shape({
          Phone: Yup.string()
            .trim()
            .matches(/^\+(?:[0-9] ?){6,14}[0-9]$/, "Incorrect format")
            .required("Required"),
          CountryCode: Yup.string().trim().required("Required"),
        }),
        SecondaryPhone: Yup.object().shape({
          Phone: Yup.string()
            .trim()
            .matches(/^\+(?:[0-9] ?){6,14}[0-9]$/, "Incorrect format"),
          CountryCode: Yup.string().trim(),
        }),
      }),
    }),
  }),
  Yup.object().shape({
    Patient: Yup.object().shape({
      Height: Yup.number()                
        .min(1, "Height can't be 0")
        .max(240, "Height must be 240 or less"),        
      Weight: Yup.number()                
        .min(1, "Weight can't be 0")
        .max(500, "Weight must be 500 or less"),        
      Allergies: Yup.array().of(
        Yup.string()
          .trim()
          .matches(/^[a-zA-Z0-9.\s]+$/, "Invalid")
          .max(100,"Must be 100 characters or less")
          .required("Cannot be empty!")
      ),
      ChronicDiseases: Yup.array().of(
        Yup.string()
          .trim()
          .matches(/^[a-zA-Z0-9.\s]+$/, "Invalid")
          .max(100,"Must be 100 characters or less")
          .required("Required")
      ),      
      CriticNotes: Yup.array().of(
        Yup.object().shape({
          DateCreated: Yup.date(),
          DateModified: Yup.date(),
          Note: Yup.string()
            .trim()
            .matches(/^[\s\S]+$/, "Invalid")
            .max(5000, "Must be 600 characters or less")
            .required("Cannot be empty!")
        })
      ),
    }),
  }),
];
